import React from "react";
import { Car, Bike, Clock, IndianRupee, MessageCircle, ShieldCheck, Wallet, CalendarClock, Star, FileCheck, Store, BarChart3, ListOrdered, Truck, Smartphone, Monitor, Tablet } from "lucide-react";
import { IMAGES } from "../../lib/constants";
import { PageHero, SectionHeading, FeatureGrid, Steps, Testimonial, CtaBand } from "../../components/marketing/Blocks";

export function ForRiders() {
  return (
    <>
      <PageHero
        eyebrow="For customers"
        title="Get around town without the haggling."
        lead="Upfront fares, verified drivers and live tracking. Choose a Scooter for quick hops, an Autorickshaw or Taxi Car for everyday trips, or a Jumbo Taxi for the whole group."
        image={IMAGES.mountainRoad}
        imageAlt="Modern taxi cab on city street"
        cta={{ to: "/auth/signup", label: "Create a customer account" }}
        secondary={{ to: "/auth/phone", label: "Sign in with phone" }}
      />
      <section className="kt-container py-16 lg:py-24">
        <SectionHeading eyebrow="What you get" title="Everything you need for the ride, and the meal after." />
        <div className="mt-12">
          <FeatureGrid items={[
            { icon: IndianRupee, title: "Fares shown before you book", text: "Distance-based pricing for every vehicle class. What you see is what you pay." },
            { icon: Car, title: "Four vehicle classes", text: "Scooter for one, Autorickshaw for three, Taxi Car for four, and Jumbo Taxi for up to six." },
            { icon: CalendarClock, title: "Scheduled rides", text: "Book early morning airport runs or late-night pickups ahead of time." },
            { icon: MessageCircle, title: "Chat with your driver", text: "Send a landmark, a gate number or a change of plan without sharing your number." },
            { icon: Wallet, title: "Promos and payments", text: "Apply promo codes, pay by cash, UPI or card, and keep every receipt in one place." },
            { icon: Star, title: "Rate every trip", text: "Your ratings keep the network honest. Drivers see them too." },
          ]} />
        </div>
      </section>
      <section className="bg-surface-200/60 py-16 lg:py-24">
        <div className="kt-container">
          <SectionHeading eyebrow="How it works" title="From your sofa to your seat in three steps." />
          <div className="mt-10"><Steps steps={[
            { title: "Set pickup and drop", text: "Search an address or drop a pin. From your doorstep to the airport, we cover it." },
            { title: "Pick a vehicle", text: "Compare fares and ETAs across Scooter, Autorickshaw, Taxi Car and Jumbo Taxi before confirming." },
            { title: "Track and ride", text: "Watch your driver arrive on the map, chat if needed, then pay and rate." },
          ]} /></div>
        </div>
      </section>
      <section className="kt-container py-16">
        <div className="grid gap-6 md:grid-cols-3">
          <Testimonial quote="I used to call three different taxi stands to find a ride to the airport. Now I book from bed and the driver is at the gate by 5." name="Aben" role="Customer" />
          <Testimonial quote="The fare is on the screen before I confirm. That alone changed how I move around town." name="Kevi" role="Customer" />
          <Testimonial quote="Ordered dinner and a ride home in the same app after a wedding. Both arrived before the speeches finished." name="Mhalo" role="Customer" />
        </div>
      </section>
      <CtaBand title="Your first ride is a tap away." text="Sign up with email or phone. New customers can use WELCOME50 on their first order." cta={{ to: "/auth/signup", label: "Get started" }} tone="brand" />
    </>
  );
}

export function ForDrivers() {
  return (
    <>
      <PageHero
        eyebrow="For drivers"
        title="Drive your hours. Earn on rides and deliveries."
        lead="Own a scooter, autorickshaw or car? Join a verified network of local drivers, go online when you want, and get paid for both passengers and food runs."
        image={IMAGES.driverPhone}
        imageAlt="Driver using navigation in a car"
        cta={{ to: "/auth/signup?role=driver", label: "Apply as a driver" }}
        secondary={{ to: "/stories", label: "Read driver stories" }}
      />
      <section className="kt-container py-16 lg:py-24">
        <SectionHeading eyebrow="Why drive with us" title="Flexible work, honest earnings, real support." />
        <div className="mt-12">
          <FeatureGrid items={[
            { icon: Clock, title: "Go online when you like", text: "Toggle online from the dashboard. No shifts, no minimum hours." },
            { icon: Truck, title: "Rides and food, one account", text: "Choose the services you offer. Fill quiet hours with delivery runs." },
            { icon: Wallet, title: "Daily earnings view", text: "See today, this week and this month at a glance, split by rides and food." },
            { icon: ShieldCheck, title: "Verified network", text: "Upload your licence, RC and insurance once. Verified drivers get the badge and the bookings." },
            { icon: Bike, title: "Scooter, auto, car or jumbo", text: "Scooter, autorickshaw, taxi car or jumbo taxi, there is a vehicle class for you." },
            { icon: Smartphone, title: "Works on web and mobile", text: "Same account on the mobile app and this desktop dashboard." },
          ]} />
        </div>
      </section>
      <section className="bg-surface-200/60 py-16 lg:py-24">
        <div className="kt-container">
          <SectionHeading eyebrow="Getting verified" title="Three documents, then you are live." />
          <div className="mt-10"><Steps steps={[
            { title: "Create a driver account", text: "Sign up with your name, phone and vehicle type." },
            { title: "Upload your documents", text: "Driving licence, vehicle RC and insurance certificate. Photos from your phone are fine." },
            { title: "Get approved", text: "Our team reviews within 24 hours. Once approved, go online and start earning." },
          ]} /></div>
        </div>
      </section>
      <section className="kt-container py-16 lg:py-24" data-testid="vehicle-eligibility">
        <SectionHeading eyebrow="Driver type & vehicle" title="Which vehicle can accept which orders." />
        <p className="mt-4 max-w-2xl text-base text-ink-soft">Your vehicle type and verification decide the work you can take. Only drivers with a valid commercial taxi permit carry passengers; food delivery runs on two-wheelers; Private Car bookings are served by Private Taxi drivers.</p>
        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-surface-300 text-ink-muted">
                <th className="py-3 pr-4 font-semibold">Driver type / vehicle</th>
                <th className="py-3 px-3 font-semibold">City rides</th>
                <th className="py-3 px-3 font-semibold">Food delivery</th>
                <th className="py-3 px-3 font-semibold">Intercity</th>
                <th className="py-3 pl-3 font-semibold">Private car (day hire)</th>
              </tr>
            </thead>
            <tbody>
              {[
                { type: "Delivery only — private scooter/bike", rides: false, food: true, intercity: false, pc: false },
                { type: "Bike / Scooter — commercial (permit)", rides: true, food: true, intercity: false, pc: false },
                { type: "Autorickshaw — commercial (up to 3)", rides: true, food: false, intercity: false, pc: false },
                { type: "Taxi Car — commercial (up to 4)", rides: true, food: false, intercity: true, pc: false },
                { type: "Jumbo Taxi — commercial (up to 6)", rides: true, food: false, intercity: true, pc: false },
                { type: "Private Taxi — Private Car (4/6-seater)", rides: false, food: false, intercity: false, pc: true },
              ].map((r) => (
                <tr key={r.type} className="border-b border-surface-200 text-ink">
                  <td className="py-3 pr-4 font-medium">{r.type}</td>
                  {[r.rides, r.food, r.intercity, r.pc].map((v, i) => (
                    <td key={i} className={`py-3 ${i === 3 ? "pl-3" : "px-3"}`}>
                      <span className={v ? "font-semibold text-brand" : "text-ink-muted"}>{v ? "Yes" : "—"}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-5 text-sm text-ink-muted">Commercial (passenger) vehicles must have an admin-approved, unexpired taxi permit. Full fee details are on the <a href="/charges" className="font-medium text-brand underline">Charges &amp; Fees</a> page.</p>
      </section>
      <CtaBand title="Start earning on your schedule." text="Verification is free and usually takes under a day." cta={{ to: "/auth/signup?role=driver", label: "Apply now" }} />
    </>
  );
}

export function ForRestaurants() {
  return (
    <>
      <PageHero
        eyebrow="For restaurants"
        title="Put your kitchen on every table in your city."
        lead="List your menu, receive orders on a desktop dashboard built for busy counters, and let our delivery partners handle the rest."
        image={IMAGES.indianFood}
        imageAlt="Spread of Indian dishes"
        cta={{ to: "/auth/signup?role=restaurant", label: "Partner with us" }}
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />
      <section className="kt-container py-16 lg:py-24">
        <SectionHeading eyebrow="Restaurant tools" title="Run the counter from one screen." />
        <div className="mt-12">
          <FeatureGrid items={[
            { icon: ListOrdered, title: "Live order board", text: "Accept, prepare, mark ready. Every order moves through clear stages." },
            { icon: Store, title: "Menu manager", text: "Add dishes, photos, prices, veg tags and availability in seconds." },
            { icon: BarChart3, title: "Analytics", text: "Revenue trends, top items, prep times and ratings, updated daily." },
            { icon: FileCheck, title: "Simple verification", text: "FSSAI licence and ownership document. Approved partners get listed." },
            { icon: MessageCircle, title: "Customer chat", text: "Clarify an order or confirm a substitution directly with the customer." },
            { icon: Truck, title: "Delivery handled", text: "Our verified delivery partners pick up and deliver. You focus on the food." },
          ]} />
        </div>
      </section>
      <section className="kt-container pb-8">
        <div className="grid gap-6 md:grid-cols-2">
          <Testimonial quote="We went from two phone lines ringing all evening to a single screen. Orders are cleaner and nothing gets lost." name="Anna's Biryani House" role="Partner restaurant" />
          <Testimonial quote="The analytics showed us Friday nights were our real peak. We staffed for it and revenue followed." name="Fresh Bowl" role="Partner restaurant" />
        </div>
      </section>
      <section className="kt-container py-16 lg:py-24" id="install" data-testid="install-guide">
        <SectionHeading eyebrow="Use on your computer or tablet" title="Install the orders board like an app. No app store needed." />
        <p className="mt-4 max-w-2xl text-base text-ink-soft">KohimaTaxi Partner installs straight from your browser on Windows, Mac, ChromeOS, Android tablets and iPad. It opens in its own window, keeps the screen awake at the counter, and updates itself.</p>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {[
            { icon: Monitor, title: "Windows, Mac or ChromeOS", steps: ["Open the dashboard in Chrome or Edge and log in.", "Click Install app in the left sidebar (or the install icon in the address bar).", "Confirm. KohimaTaxi now opens from your desktop or Start menu."] },
            { icon: Tablet, title: "Android tablet", steps: ["Open the dashboard in Chrome and log in.", "Tap Install app on the Orders board banner, or Chrome menu → Add to Home screen.", "Launch it from your home screen. It runs full-screen."] },
            { icon: Smartphone, title: "iPad (Safari)", steps: ["Open the dashboard in Safari and log in.", "Tap the Share button, then Add to Home Screen.", "Tap Add. The icon appears on your home screen."] },
          ].map((d) => (
            <div key={d.title} className="kt-card p-6" data-testid={`install-guide-${d.title.split(" ")[0].toLowerCase().replace(",", "")}`}>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand"><d.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 text-lg font-semibold text-ink">{d.title}</h3>
              <ol className="mt-3 space-y-2 text-sm text-ink-soft">{d.steps.map((s, i) => <li key={i} className="flex gap-2.5"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-200 text-[11px] font-bold text-ink">{i + 1}</span><span>{s}</span></li>)}</ol>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-ink-muted">Tip: on the Orders board, turn on <strong>Screen awake</strong> and <strong>Full screen</strong> so a counter tablet never misses a new-order chime.</p>
      </section>
      <CtaBand title="Bring your menu online." text="Create a restaurant account, upload two documents, and start receiving orders." cta={{ to: "/auth/signup?role=restaurant", label: "Become a partner" }} tone="brand" />
    </>
  );
}
